import { AppErrors } from '../../core/error/appError';
import type { UserProfile } from '../../core/model/search';
import { failure, success, type AppResult } from '../../core/result/appResult';
import { PhoneNormalizer } from '../../core/util/phoneNormalizer';
import type { ApiService } from '../api/apiService';
import { clearSession, loadSession, saveSession } from '../local/sessionStore';
import { userToDomain } from '../mappers';
import { execute } from './apiCall';
import type { AuthRepository, Session, TelegramLink, TelegramLinkState } from './repositories';

/**
 * Sesión del usuario.
 *
 * Reservar es la única operación que la exige; todo el descubrimiento funciona como invitado,
 * así que el estado por defecto es `ANONYMOUS` y nunca se fuerza identificarse al abrir la app.
 */
export class DefaultAuthRepository implements AuthRepository {
  private current: Session;
  private token: string | null;
  private readonly listeners = new Set<(session: Session) => void>();

  constructor(private readonly api: ApiService) {
    const stored = loadSession();
    this.token = stored?.accessToken ?? null;
    this.current = stored?.user ? { kind: 'AUTHENTICATED', user: stored.user } : { kind: 'ANONYMOUS' };
  }

  /** Lo consume el cliente HTTP para poner la cabecera `Authorization`. */
  accessToken(): string | null {
    return this.token;
  }

  session(): Session {
    return this.current;
  }

  subscribe(listener: (session: Session) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async startTelegramLink(name: string, returnUrl?: string): Promise<AppResult<TelegramLink>> {
    const result = await execute(() => this.api.startTelegramLink(name.trim(), returnUrl));
    if (!result.ok) return result;
    return success({
      token: result.data.token,
      url: result.data.url,
      expiresInSeconds: result.data.expires_in,
    });
  }

  async pollTelegramLink(token: string): Promise<AppResult<TelegramLinkState>> {
    const result = await execute(() => this.api.telegramLinkStatus(token));
    if (!result.ok) return result;

    const { status, user, access_token: accessToken } = result.data;
    if (status !== 'VERIFIED') return success({ status });
    if (!user) return failure(AppErrors.unknown('Verificación sin usuario'));

    // Llega una sola vez: aquí es donde la sesión queda iniciada.
    const profile = userToDomain(user);
    this.persist(accessToken ?? null, profile);
    return success({ status: 'VERIFIED', profile });
  }

  async identify(name: string, phone: string): Promise<AppResult<UserProfile>> {
    // Se normaliza aquí, en el borde: así "55 1234 5678" y "+52 55 1234 5678" son la misma
    // cuenta, en lugar de dos según cómo lo haya tecleado la persona ese día.
    const normalized = PhoneNormalizer.normalize(phone) ?? phone.trim();
    return this.authenticate(() => this.api.identify({ name: name.trim(), phone: normalized }));
  }

  logout(): void {
    this.token = null;
    clearSession();
    this.emit({ kind: 'ANONYMOUS' });
  }

  async getProfile(): Promise<AppResult<UserProfile>> {
    const result = await execute(() => this.api.getProfile());
    if (!result.ok) {
      if (result.error.kind === 'SESSION_EXPIRED') this.logout();
      return result;
    }
    const profile = userToDomain(result.data);
    this.persist(this.token, profile);
    return success(profile);
  }

  async updateProfile(profile: UserProfile): Promise<AppResult<UserProfile>> {
    const result = await execute(() =>
      this.api.updateProfile({
        id: profile.id,
        name: profile.fullName,
        email: profile.email,
        phone: profile.phone,
        favorite_cuisines: [...profile.favoriteCuisines],
        default_party_size: profile.defaultPartySize,
      }),
    );
    if (!result.ok) return result;
    const updated = userToDomain(result.data);
    this.persist(this.token, updated);
    return success(updated);
  }

  private async authenticate(
    call: () => ReturnType<ApiService['identify']>,
  ): Promise<AppResult<UserProfile>> {
    const result = await execute(call);
    if (!result.ok) return result;
    const dto = result.data.user;
    if (!dto) return failure(AppErrors.unknown('Respuesta de sesión sin usuario'));
    const profile = userToDomain(dto);
    this.persist(result.data.access_token, profile);
    return success(profile);
  }

  private persist(token: string | null, user: UserProfile): void {
    this.token = token;
    if (token) saveSession({ accessToken: token, user });
    this.emit({ kind: 'AUTHENTICATED', user });
  }

  private emit(session: Session): void {
    this.current = session;
    this.listeners.forEach((listener) => listener(session));
  }
}
